import { useLocale } from '../../application/i18n/LocaleProvider';
import { DECISION_PROPERTIES, propertyNoteKey } from '../../domain/plants/plantProperties';
import { PROPERTY_ICONS, SprigIcon } from './PlantIcons';
import { propertyNote, propertyValue } from './plantPropertyText';
import { ChevronRightIcon } from '../chrome/BrandIcons';

/**
 * The details: what each decision actually MEANS for this plant (FR-06).
 *
 * Below the fold since SPEC-024, five rows rather than four columns and a strip
 * since PR-24.3, and sitting between About and Care tips since PR-24.4 — the
 * page answers what the plant is before it answers what it wants.
 *
 * The first screen states the four in the short vocabulary; this is where the
 * full value and its one plain sentence live, stated once so the two surfaces
 * cannot drift.
 *
 * Rows, because a sentence in a quarter of a phone is four words on six lines —
 * the column that read "Adaptabl / e" is what this replaces. Five of them in one
 * shape, where maintenance used to be the odd row out at the foot of a grid.
 *
 * The sentence itself replaces SPEC-002's three-dot axis (SPEC-003 D-1): the dots
 * were `aria-hidden` decoration because a rounded 1-of-3 is not a fact, so the
 * screen reader heard the label alone. The sentence is the same information in
 * words, and everybody gets it.
 */

function DecisionColumn({ plant, property }) {
    const { t } = useLocale();
    const PropertyIcon = PROPERTY_ICONS[property];
    // Composed in `plantPropertyText.js` since SPEC-024, so the three glances on
    // the first screen say what this column says.
    const value = propertyValue(t, plant, property);
    const note = propertyNote(t, plant, property);

    return (
        <li className={`decision decision--${property}`}>
            <PropertyIcon size={26} />
            <span className="decision__heading">
                <span className="decision__name">{t(`plants.property.${property}`)}</span>
                <span className="decision__value">{value}</span>
            </span>
            <span className="decision__note">{note}</span>
        </li>
    );
}

export default function DecisionPlate({ plant, careTipsId }) {
    const { t } = useLocale();
    const { maintenance } = plant.properties;
    // Deliberately NOT `prose.care.maintenance`: the packs fill that field with
    // the same short label the value beside it already shows — "Easy", "Low once
    // established" — which put the identical two words twice on one row.
    const note = t(propertyNoteKey(maintenance.labelKey));

    return (
        <section className="decision-plate">
            {/* Titled, like the two panels under it. Three cards with three
                headings is a page a reader can skim; a nameless plate followed
                by two named ones is a page that starts mid-sentence. */}
            <h2 className="decision-plate__title">{t('plants.detail.details')}</h2>
            <ul className="decision-plate__row">
                {DECISION_PROPERTIES.map((property) => (
                    <DecisionColumn key={property} plant={plant} property={property} />
                ))}
            </ul>
            {/* The reference draws a chevron here, which promises somewhere to go.
                Care tips is that somewhere — the panel that says what the work
                actually is — rather than a control that only looks tappable. */}
            <a className="decision-plate__maintenance" href={`#${careTipsId}`}>
                <SprigIcon size={30} />
                <span className="decision__heading">
                    <span className="decision__name">{t('plants.property.maintenance')}</span>
                    <span className="decision__value">{t(maintenance.labelKey)}</span>
                </span>
                <span className="decision__note">{note}</span>
                <ChevronRightIcon size={20} />
            </a>
        </section>
    );
}
